import { execFileSync, spawn } from 'node:child_process'
import fs from 'node:fs'
import path from 'node:path'
import { stripVTControlCharacters } from 'node:util'
import type { CaptureResult, PackageManager } from '@cli-types/core-engine/utils.js'
import { REDACTED } from '@cli-constants/cli.js'
import { LOCKFILE_MAP } from '@cli-constants/paths.js'
import { findUpwards } from '@cli-utils/files.js'

export function detectPackageManager(cwd: string): PackageManager {
  return (
    findUpwards(cwd, (dir) => {
      for (const [lockfile, pm] of Object.entries(LOCKFILE_MAP)) {
        if (fs.existsSync(path.join(dir, lockfile))) return pm
      }

      const pkgPath = path.join(dir, 'package.json')
      if (!fs.existsSync(pkgPath)) return undefined

      try {
        const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8')) as Record<string, unknown>
        if (typeof pkg.packageManager === 'string') {
          const name = pkg.packageManager.split('@')[0]
          if (name === 'pnpm' || name === 'npm' || name === 'yarn' || name === 'bun') return name
        }
      } catch {}

      return undefined
    }) ?? 'npm'
  )
}

export function installCommand(pm: PackageManager): string {
  return pm === 'yarn' ? 'yarn' : `${pm} install`
}

export function addCommand(pm: PackageManager, deps: string[], dev = false): string {
  const devFlag = dev ? (pm === 'yarn' ? '--dev' : '-D') : ''

  switch (pm) {
    case 'pnpm':
      return ['pnpm', 'add', devFlag, ...deps].filter(Boolean).join(' ')
    case 'yarn':
      return ['yarn', 'add', devFlag, ...deps].filter(Boolean).join(' ')
    case 'bun':
      return ['bun', 'add', devFlag, ...deps].filter(Boolean).join(' ')
    default:
      return ['npm', 'install', devFlag, ...deps].filter(Boolean).join(' ')
  }
}

export function runCommand(pm: PackageManager, script: string): string {
  switch (pm) {
    case 'pnpm':
      return `pnpm ${script}`
    case 'yarn':
      return `yarn ${script}`
    case 'bun':
      return `bun run ${script}`
    default:
      return `npm run ${script}`
  }
}

/** `pnpm x` / `yarn x` / `bunx x` / `npx x` — running a binary, not a package script. */
export function pmExecCommand(pm: PackageManager, command: string): string {
  switch (pm) {
    case 'pnpm':
      return `pnpm ${command}`.trim()
    case 'yarn':
      return `yarn ${command}`.trim()
    case 'bun':
      return `bunx ${command}`.trim()
    default:
      return `npx ${command}`.trim()
  }
}

export function execPm(pm: PackageManager, args: string[], cwd: string): void {
  execFileSync(pm, args, { cwd, stdio: 'inherit' })
}

/** The Vercel and Railway CLIs wrap their JSON in banners, prompts, and progress lines. */
export function parseCliJson<T = unknown>(output: string): T | undefined {
  const trimmed = stripVTControlCharacters(output).trim()
  if (!trimmed) return undefined

  try {
    return JSON.parse(trimmed) as T
  } catch {}

  const span = extractOutermostJson(trimmed)
  if (span === undefined) return undefined

  try {
    return JSON.parse(span) as T
  } catch {
    return undefined
  }
}

function extractOutermostJson(text: string): string | undefined {
  const starts = [text.indexOf('{'), text.indexOf('[')].filter((index) => index >= 0)
  if (starts.length === 0) return undefined

  const start = Math.min(...starts)
  // Match the closer to the opener. Taking the last of either would pick up a
  // stray bracket in a trailing banner line.
  const end = text[start] === '{' ? text.lastIndexOf('}') : text.lastIndexOf(']')
  if (end <= start) return undefined

  return text.slice(start, end + 1)
}

/** `fallback` is returned when the value does not parse as a URL at all. */
export function maskUrlCredentials(url: string, fallback: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.protocol}//${parsed.host}/***`
  } catch {
    return fallback
  }
}

export function openBrowser(url: string): void {
  try {
    const platform = process.platform
    if (platform === 'darwin') {
      execFileSync('open', [url], { stdio: 'ignore' })
    } else if (platform === 'win32') {
      execFileSync('cmd', ['/c', 'start', url], { stdio: 'ignore' })
    } else {
      execFileSync('xdg-open', [url], { stdio: 'ignore' })
    }
  } catch {}
}

export function outputTail(output: string, maxLines = 20): string | undefined {
  const lines = stripVTControlCharacters(output)
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0)
  return lines.length > 0 ? lines.slice(-maxLines).join('\n') : undefined
}

export function resolveLocalBin(cwd: string, name: string): string | null {
  const binPath = path.join(
    cwd,
    'node_modules',
    '.bin',
    process.platform === 'win32' ? `${name}.cmd` : name
  )
  return fs.existsSync(binPath) ? binPath : null
}

export function redactSecrets(text: string): string {
  return text
    .replace(/(\b[a-z][a-z0-9+.-]*:\/\/[^\s:@/]+:)[^\s@]+@/gi, `$1${REDACTED}@`)
    .replace(/\b(bearer\s+)[a-z0-9._~+/=-]+/gi, `$1${REDACTED}`)
    .replace(
      /(\b[\w-]*(?:token|secret|password|passwd|api[-_]?key|apikey)[\w-]*\s*[=:]\s*)(["']?)[^\s"']{4,}\2/gi,
      `$1$2${REDACTED}$2`
    )
}

export function runCapture(
  bin: string,
  args: string[],
  options: {
    cwd: string
    timeoutMs: number
    env?: NodeJS.ProcessEnv
  }
): Promise<CaptureResult> {
  return new Promise((resolve) => {
    const child = spawn(bin, args, {
      cwd: options.cwd,
      stdio: 'pipe',
      env: {
        ...process.env,
        ...options.env
      }
    })

    let stdout = ''
    let stderr = ''
    let timedOut = false

    child.stdout?.on('data', (chunk: Buffer) => {
      stdout += chunk.toString()
    })

    child.stderr?.on('data', (chunk: Buffer) => {
      stderr += chunk.toString()
    })

    const timeout = setTimeout(() => {
      timedOut = true
      child.kill('SIGTERM')
    }, options.timeoutMs)

    child.on('close', (code, signal) => {
      clearTimeout(timeout)

      const output = [stdout.trimEnd(), stderr.trimEnd()].filter(Boolean).join('\n')

      if (timedOut) {
        resolve({
          success: false,
          error: `Command timed out after ${Math.floor(options.timeoutMs / 1000)} seconds`,
          output
        })
        return
      }

      if (signal) {
        resolve({
          success: false,
          error: `Command was terminated by ${signal}`,
          output
        })
        return
      }

      if (code === 0) {
        resolve({
          success: true,
          error: '',
          output
        })
        return
      }

      resolve({
        success: false,
        error: `Command exited with code ${code ?? 'unknown'}`,
        output
      })
    })

    child.on('error', (err) => {
      clearTimeout(timeout)
      const output = [stdout.trimEnd(), stderr.trimEnd()].filter(Boolean).join('\n')
      resolve({
        success: false,
        error: err.message,
        output
      })
    })
  })
}
